const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatTime = (value, fallback = "") => {
  if (!value) return fallback;
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : fallback;
};

export const defaultExportTimeframeForMonth = (month) => {
  const [year, monthIndex] = month.split("-").map(Number);
  return {
    mode: "month",
    month,
    start_date: `${month}-01`,
    end_date: toDateKey(new Date(year, monthIndex, 0)),
  };
};

export const defaultDraft = (entryKind = "") => ({
  entry_kind: entryKind,
  start_time: "08:00",
  end_time: "17:00",
  is_full_day: true,
  holiday_type_id: "",
  start_date: "",
  end_date: "",
  comment: "",
});

/*
  Welcher Zeitraum beim Speichern von Ferien geschrieben wird.

  Die Von/Bis-Felder im Editor sind bei Ferien die Wahrheit: openEditor setzt sie
  auf die Auswahl, wer sie danach aufzieht, meint genau diesen Zeitraum — vorher
  gewann immer die Auswahl, ein aufgezogener Zeitraum landete also nur auf dem
  angeklickten Tag. Solange die Felder unveraendert der Auswahl entsprechen,
  entscheidet weiterhin die Auswahl; nicht zusammenhaengende Tage bleiben so
  einzelne Ferieneintraege statt eines Blocks ueber die Luecke hinweg (null).
*/
export const holidaySaveRange = ({
  draftStartDate,
  draftEndDate,
  selectionDates = [],
  userDates = [],
  contiguous = false,
}) => {
  const selectionStart = selectionDates[0];
  const selectionEnd = selectionDates[selectionDates.length - 1];
  const startDate = draftStartDate || selectionStart;
  const endDate = draftEndDate || startDate;
  if (!startDate) return null;

  if (startDate !== selectionStart || endDate !== selectionEnd) {
    return { start_date: startDate, end_date: endDate };
  }
  if (contiguous && userDates.length) {
    return { start_date: userDates[0], end_date: userDates[userDates.length - 1] };
  }
  return null;
};

export const defaultSettingsDraft = () => ({
  location_name: "",
  user: {
    availability_mode: "",
    approval_required: false,
  },
  company: {
    availability_mode: "absence",
    approval_required: false,
    edit_lock_enabled: false,
    edit_lock_day_of_month: "",
    edit_lock_horizon_months: "",
  },
  freeze_periods: [],
});

export const draftFromSettings = (settings) => ({
  location_name: settings?.location_name || "",
  user: {
    availability_mode: settings?.user?.has_user_setting ? settings.user.availability_mode : "",
    approval_required: Boolean(settings?.user?.approval_required),
  },
  company: {
    availability_mode: settings?.company?.availability_mode || "absence",
    approval_required: Boolean(settings?.company?.approval_required),
    edit_lock_enabled: Boolean(settings?.company?.edit_lock_enabled),
    edit_lock_day_of_month: settings?.company?.edit_lock_day_of_month ?? "",
    edit_lock_horizon_months: settings?.company?.edit_lock_horizon_months ?? "",
  },
  freeze_periods: settings?.freeze_periods || [],
});

export const settingsPayloadFromDraft = (draft = {}) => ({
  user: draft.user || {},
  company: draft.company || {},
  freeze_periods: draft.freeze_periods || [],
  settings_scope: "company",
});

export const draftFromDay = (day, holidayTypes) => {
  if (!day || day.status === "empty") return defaultDraft("");
  if (day.status === "holiday" && day.holidays?.[0]) {
    return {
      ...defaultDraft("holiday"),
      holiday_type_id: day.holidays[0].holiday_type_id || holidayTypes?.[0]?.id || "",
      start_date: day.holidays[0].start_date || day.date || "",
      end_date: day.holidays[0].end_date || day.date || "",
    };
  }

  const entry = day.entries?.[0] || day.default_entries?.[0];
  if (!entry) {
    return ["availability", "absence", "free_request"].includes(day.status)
      ? defaultDraft(day.status)
      : defaultDraft("");
  }

  return {
    ...defaultDraft(entry.kind),
    start_time: entry.is_full_day ? "08:00" : formatTime(entry.start_time, "08:00"),
    end_time: entry.is_full_day ? "17:00" : formatTime(entry.end_time, "17:00"),
    is_full_day: Boolean(entry.is_full_day),
    comment: entry.comment || "",
  };
};
